import { useState, useCallback, useMemo } from 'react'
import { Plus, Users, ShieldUser, UserCheck, UserLock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/DataTable'
import { UserFormDialog } from '../components/UserFormDialog'
import { UserDeleteDialog } from '../components/UserDeleteDialog'
import { UserViewDialog } from '../components/UserViewDialog'
import { ChangePasswordModal } from '../components/ChangePasswordModal'
import { useUsers, useDeleteUser } from '../hooks'
import { useUser, hasPermission } from '@/app/store/auth'
import { useTimezoneDate } from '@/hooks/useTimezoneDate'
import { createUserColumns } from '../columns'
import { AdminRoute } from '@/components/auth/AdminRoute'
import type { User } from '@/types/entities'

export function UserListPage() {
    const { t } = useTranslation()
    const { formatDate } = useTimezoneDate()
    const [selectedUser, setSelectedUser] = useState<User | null>(null)
    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showViewDialog, setShowViewDialog] = useState(false)
    const [changePasswordUser, setChangePasswordUser] = useState<User | null>(null)

    const currentUser = useUser()
    const canChangePassword = hasPermission(currentUser, 'users', 'changepassword')
    const { data: usersResponse, isLoading } = useUsers()
    const deleteUser = useDeleteUser()

    const users = usersResponse?.data || []
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'standby'>('all')

    const filteredUsers = useMemo(() => {
        if (statusFilter === 'all') return users
        if (statusFilter === 'active') return users.filter((u) => u.isActive)
        return users.filter((u) => u.standby === true)
    }, [users, statusFilter])


    // Memoize callbacks to prevent re-creating on every render
    const handleEdit = useCallback((user: User) => {
        setSelectedUser(user)
        setShowEditDialog(true)
    }, [])

    const handleDelete = useCallback((user: User) => {
        setSelectedUser(user)
        setShowDeleteDialog(true)
    }, [])

    const handleView = useCallback((user: User) => {
        setSelectedUser(user)
        setShowViewDialog(true)
    }, [])

    const handleChangePassword = useCallback((user: User) => {
        setChangePasswordUser(user)
    }, [])

    const handleDeleteConfirm = useCallback(() => {
        if (selectedUser) {
            deleteUser.mutate(selectedUser.id, {
                onSuccess: () => {
                    setShowDeleteDialog(false)
                    setSelectedUser(null)
                },
            })
        }
    }, [selectedUser, deleteUser])

    const handleExport = useCallback(() => {
        // TODO: Implement CSV export
        console.log('Export users to CSV')
    }, [])

    // Memoize columns to prevent re-creation
    const columns = useMemo(
        () => createUserColumns(
            handleEdit,
            handleDelete,
            handleView,
            t,
            formatDate,
            canChangePassword ? handleChangePassword : undefined
        ),
        [handleEdit, handleDelete, handleView, t, formatDate, canChangePassword, handleChangePassword]
    )

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('users.loadingUsers')}</div>
            </div>
        )
    }

    return (
        <AdminRoute>
            <div className="space-y-[var(--spacing-section)]" style={{ padding: 'var(--spacing-page)' }}>
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl lg:text-2xl font-bold">{t('users.title')}</h1>
                        <p className="text-muted-foreground">
                            {t('users.description')}
                        </p>
                    </div>
                    <div className="flex space-x-2">
                        <Button onClick={() => setShowCreateDialog(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            {t('users.createUser')}
                        </Button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid gap-4 md:grid-cols-4">
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <Users className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('users.totalUsers')}</p>
                                <p className="text-2xl font-bold">{users.length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <ShieldUser className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('users.administrators')}</p>
                                <p className="text-2xl font-bold">
                                    {users.filter(user => user.roles.some(role => role.name === 'admin')).length}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <UserCheck className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('users.activeUsers')}</p>
                                <p className="text-2xl font-bold">
                                    {users.filter(user => user.isActive).length}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <UserLock className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('users.standbyUsers')}</p>
                                <p className="text-2xl font-bold">
                                    {users.filter(user => user.standby === true).length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table - Full Width - Extended */}
                <div 
                    className="overflow-x-auto" 
                    style={{ 
                        marginLeft: 'calc(var(--spacing-page) * -2)',
                        marginRight: 'calc(var(--spacing-page) * -2)',
                        paddingLeft: 'calc(var(--spacing-page) * 0.05)',
                        paddingRight: 'calc(var(--spacing-page) * 0.05)',
                        width: 'calc(100vw - var(--sidebar-width-expanded) - var(--spacing-page) * 0.1)',
                        maxWidth: 'none'
                    }}
                >
                    <DataTable
                        columns={columns}
                        data={filteredUsers}
                        searchKey="name"
                        searchPlaceholder={t('users.searchPlaceholder')}
                        onExport={handleExport}
                        exportButtonText={t('users.export')}
                        enableRowSelection={false}
                        toolbarContent={
                            <div className="flex items-center gap-6 ml-8">
                                <span className="text-sm font-medium whitespace-nowrap">{t('users.filterBy', 'Filter by')}:</span>
                                <div className="flex rounded-lg border p-0.5 bg-muted/30 gap-2" role="group">
                                    <button
                                        type="button"
                                        onClick={() => setStatusFilter('all')}
                                        className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                            statusFilter === 'all' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                        <Users className="h-4 w-4 text-blue-600" />
                                        {t('users.filterAll')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setStatusFilter('active')}
                                        className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                            statusFilter === 'active' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                        <UserCheck className="h-4 w-4 text-blue-600" />
                                        {t('users.filterActive')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setStatusFilter('standby')}
                                        className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                            statusFilter === 'standby' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                        <UserLock className="h-4 w-4 text-blue-600" />
                                        {t('users.filterStandby')}
                                    </button>
                                </div>
                            </div>
                        }
                    />
                </div>

                {/* Dialogs */}
                <UserFormDialog
                    open={showCreateDialog}
                    onOpenChange={setShowCreateDialog}
                    user={null}
                />

                <UserFormDialog
                    open={showEditDialog}
                    onOpenChange={setShowEditDialog}
                    user={selectedUser}
                />

                <UserDeleteDialog
                    open={showDeleteDialog}
                    onOpenChange={setShowDeleteDialog}
                    user={selectedUser}
                    onConfirm={handleDeleteConfirm}
                    isLoading={deleteUser.isPending}
                />

                <UserViewDialog
                    open={showViewDialog}
                    onOpenChange={setShowViewDialog}
                    user={selectedUser}
                />

                {changePasswordUser && (
                    <ChangePasswordModal
                        open={!!changePasswordUser}
                        onOpenChange={(open) => !open && setChangePasswordUser(null)}
                        user={changePasswordUser}
                        currentUserId={currentUser?.id}
                    />
                )}
            </div>
        </AdminRoute>
    )
}



